import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Sparkles, AlertCircle, CheckCircle, LogIn, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GiftRedeemPage = () => {
  const { giftCode } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeemStatus, setRedeemStatus] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    checkAuthAndFetchGift();
  }, [giftCode]);

  const checkAuthAndFetchGift = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // Fetch gift details
    await fetchGiftDetails();
  };

  const fetchGiftDetails = async () => {
    try {
      // Use token endpoint for secure lookup
      const response = await fetch(`${API_URL}/api/gifts/token/${giftCode}`);
      const data = await response.json();
      
      if (response.ok) {
        setGift(data);
      } else if (response.status === 429) {
        setError('Çok fazla istek. Lütfen bir dakika bekleyin.');
      } else if (response.status === 403) {
        setError('Geçici olarak engellendiniz. Lütfen daha sonra tekrar deneyin.');
      } else {
        setError(data.detail || 'Hediye bulunamadı');
      }
    } catch (err) {
      setError('Bağlantı hatası. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Save gift code and redirect to login
      localStorage.setItem('pendingGiftCode', giftCode);
      localStorage.setItem('returnAfterAuth', `/gift/redeem/${giftCode}`);
      navigate('/?action=login&reason=gift');
      return;
    }

    setRedeeming(true);
    try {
      const response = await fetch(`${API_URL}/api/gifts/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ gift_code: giftCode })
      });

      const data = await response.json();
      
      if (response.ok) {
        setRedeemStatus({
          success: true,
          message: data.message,
          credits: data.credits_added,
          giftName: data.gift_name,
          giftIcon: data.gift_icon,
          readingPath: data.reading_path,
          senderName: data.sender_name,
          senderMessage: data.sender_message
        });
      } else {
        if (response.status === 401) {
          // Auth required
          localStorage.setItem('pendingGiftCode', giftCode);
          localStorage.setItem('returnAfterAuth', `/gift/redeem/${giftCode}`);
          navigate('/?action=login&reason=gift');
          return;
        }
        setRedeemStatus({
          success: false,
          message: data.detail || 'Hediye kullanılamadı'
        });
      }
    } catch (err) {
      setRedeemStatus({
        success: false,
        message: 'Bağlantı hatası. Lütfen tekrar deneyin.'
      });
    } finally {
      setRedeeming(false);
    }
  };

  const handleStartReading = () => {
    if (redeemStatus?.readingPath) {
      navigate(redeemStatus.readingPath);
    } else {
      navigate('/');
    }
  };

  const handleLogin = () => {
    localStorage.setItem('pendingGiftCode', giftCode);
    localStorage.setItem('returnAfterAuth', `/gift/redeem/${giftCode}`);
    navigate('/?action=login&reason=gift');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(139, 92, 246, 0.3)',
            borderTopColor: '#8b5cf6',
            borderRadius: '50%'
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
          <h2 style={{ color: 'white', margin: '0 0 8px' }}>Hediye Bulunamadı</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            Ana Sayfaya Git
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '28px',
          padding: '40px 32px',
          textAlign: 'center',
          maxWidth: '420px',
          width: '100%'
        }}
      >
        {/* Gift Icon */}
        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 200 }}
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 20px 40px rgba(236, 72, 153, 0.3)'
          }}
        >
          <span style={{ fontSize: '48px' }}>{gift?.gift_icon || '🎁'}</span>
        </motion.div>

        {/* Redeemed Status */}
        {redeemStatus ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {redeemStatus.success ? (
              <>
                <CheckCircle size={56} color="#22c55e" style={{ marginBottom: '16px' }} />
                <h2 style={{ color: 'white', margin: '0 0 8px', fontSize: '24px' }}>
                  Tebrikler! 🎉
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 8px', fontSize: '16px' }}>
                  <strong style={{ color: '#22c55e' }}>{redeemStatus.credits} kredi</strong> hesabına eklendi!
                </p>
                
                {redeemStatus.senderName && (
                  <p style={{ color: '#c084fc', margin: '0 0 16px', fontSize: '14px' }}>
                    {redeemStatus.senderName} tarafından gönderildi
                  </p>
                )}
                
                {redeemStatus.senderMessage && (
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    margin: '16px 0',
                    fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '14px'
                  }}>
                    &ldquo;{redeemStatus.senderMessage}&rdquo;
                  </div>
                )}
                
                <button
                  onClick={handleStartReading}
                  style={{
                    width: '100%',
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                    border: 'none',
                    borderRadius: '14px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    marginTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {redeemStatus.giftName} Başlat
                  <ArrowRight size={20} />
                </button>
              </>
            ) : (
              <>
                <AlertCircle size={56} color="#ef4444" style={{ marginBottom: '16px' }} />
                <h2 style={{ color: 'white', margin: '0 0 8px', fontSize: '24px' }}>
                  Bir Sorun Oluştu
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px' }}>
                  {redeemStatus.message}
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '14px 28px',
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '15px'
                  }}
                >
                  Ana Sayfaya Git
                </button>
              </>
            )}
          </motion.div>
        ) : (
          <>
            {/* Gift Status Check */}
            {gift?.status === 'redeemed' ? (
              <>
                <h2 style={{ color: 'white', margin: '0 0 8px', fontSize: '24px' }}>
                  Hediye Kullanılmış
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px' }}>
                  Bu hediye daha önce kullanılmış.
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}
                >
                  Kendi Falına Bak
                </button>
              </>
            ) : gift?.status === 'expired' ? (
              <>
                <h2 style={{ color: 'white', margin: '0 0 8px', fontSize: '24px' }}>
                  Hediyenin Süresi Dolmuş
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px' }}>
                  Maalesef bu hediye artık geçerli değil.
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}
                >
                  Kendi Falına Bak
                </button>
              </>
            ) : (
              <>
                {/* Active Gift */}
                <h2 style={{ color: 'white', margin: '0 0 8px', fontSize: '26px' }}>
                  Sana Bir Hediye Var! 🎁
                </h2>
                
                {gift?.sender_name && (
                  <p style={{ color: '#c084fc', margin: '0 0 16px', fontSize: '16px', fontWeight: '500' }}>
                    {gift.sender_name} sana hediye gönderdi
                  </p>
                )}

                {/* Gift Details */}
                <div style={{
                  background: 'rgba(139, 92, 246, 0.15)',
                  borderRadius: '16px',
                  padding: '20px',
                  margin: '20px 0'
                }}>
                  <div style={{ 
                    color: 'white', 
                    fontSize: '20px', 
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    {gift?.gift_name}
                  </div>
                  <div style={{ 
                    color: '#22c55e', 
                    fontSize: '15px',
                    fontWeight: '500'
                  }}>
                    +{gift?.credits_value} Kredi
                  </div>
                </div>

                {/* Personal Message */}
                {gift?.message && (
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    padding: '16px',
                    margin: '16px 0',
                    fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '15px'
                  }}>
                    &ldquo;{gift.message}&rdquo;
                  </div>
                )}

                {/* Auth Check */}
                {!isLoggedIn ? (
                  <>
                    <p style={{ 
                      color: 'rgba(255,255,255,0.5)', 
                      fontSize: '13px', 
                      margin: '16px 0'
                    }}>
                      Hediyeyi almak için giriş yapman gerekiyor
                    </p>
                    <button
                      onClick={handleLogin}
                      style={{
                        width: '100%',
                        padding: '18px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        border: 'none',
                        borderRadius: '14px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '17px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)'
                      }}
                    >
                      <LogIn size={22} />
                      Giriş Yap ve Hediyeyi Al
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleRedeem}
                    disabled={redeeming}
                    style={{
                      width: '100%',
                      padding: '18px',
                      background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                      border: 'none',
                      borderRadius: '14px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '17px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      marginTop: '24px',
                      boxShadow: '0 10px 30px rgba(236, 72, 153, 0.3)',
                      opacity: redeeming ? 0.7 : 1
                    }}
                  >
                    {redeeming ? (
                      'Hediye Alınıyor...'
                    ) : (
                      <>
                        <Sparkles size={22} />
                        Hediyemi Al
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default GiftRedeemPage;
